import {ChangeDetectorRef, Component, ElementRef, HostListener, Input, OnDestroy, inject, output, viewChild, ChangeDetectionStrategy} from "@angular/core";
import {animate, style, transition, trigger} from "@angular/animations";
import {DropdownTriggerDirective} from "./dropdown-trigger.directive";

/** Gap kept between the dropdown and the edge it is capped against. */
const BOUNDARY_MARGIN_PX = 16;

/**
 * Floor of the computed height: under this the dropdown would be a useless
 * sliver, so it is better to let it overflow than to shrink further.
 */
const MIN_AVAILABLE_HEIGHT_PX = 160;

@Component({
	selector: "app-dropdown",
	templateUrl: "./dropdown.component.html",
	styleUrls: ["./dropdown.component.scss"],
	changeDetection: ChangeDetectionStrategy.Eager,
	animations: [
		trigger('in-out-animation', [
			transition(':enter', [
				style({ opacity: 0.01 }),
				animate('300ms ease-in-out', style({ opacity: 1 }))
			]),
			transition(':leave', [
				animate('300ms ease-in-out', style({ display: 'none', opacity: 0 }))
			])
		])
	]
})
export class DropdownComponent implements OnDestroy {
	private elementRef = inject(ElementRef);
	private changeDetector = inject(ChangeDetectorRef);

	@Input() minimumMargin = 35;
	@Input() arrow = false;
	@Input() paddingX = 20;
	@Input() paddingY = 20;
	@Input() animate = true;
	@Input() radius = 9;
	// When true, the dropdown content is anchored to the bottom of the host element
	// so it grows upward instead of downward. Useful for triggers placed near the
	// bottom of the viewport (e.g. user menu at the bottom of the side menu).
	@Input() openAbove = false;
	// When true, the dropdown host is vertically aligned with the trigger element
	// on every open. This avoids hardcoded `top`/`bottom` offsets that drift when
	// the surrounding layout changes (e.g. items conditionally hidden in the side
	// menu). The dropdown host element must have a positioned ancestor.
	@Input() alignToTrigger = false;
	readonly closed = output<void>();
	readonly opened = output<void>();

	translationX = 0;

	display = false;
	protected triggerDirective: DropdownTriggerDirective | undefined;

	arrowPosition = 0;

	readonly dropdownContainer = viewChild<ElementRef>('dropdownContainer');

	private onGlobalClickBound = this.onGlobalClick.bind(this);

	ngOnDestroy(): void {
		this.removeClickListener();
	}

	toggle(trigger?: DropdownTriggerDirective) {
		this.triggerDirective = trigger;
		this.display = !this.display;

		this.repositionArrow();

		if (this.display) {
			// Repart d'une position vierge : le décalage anti-débordement est
			// recalculé pour la géométrie actuelle (sinon un décalage calculé
			// sur une fenêtre étroite collait au dropdown jusqu'au reload).
			this.translationX = 0;
			this.alignVerticallyToTrigger();
			this.scheduleAvailableHeightUpdate();
			this.addClickListener();
			this.opened.emit()
		} else {
			this.removeClickListener();
			this.closed.emit()
		}
	}

	@HostListener('window:resize')
	onWindowResize(): void {
		if (this.display) {
			this.updateAvailableHeight();
		}
	}

	private repositionArrow() {
		if (this.triggerDirective) {
			const triggerWidth = this.triggerDirective.getWidth();
			const arrowWidth = 15;
			this.arrowPosition = (triggerWidth / 2) - (arrowWidth / 2);
		}
	}

	onAnimationStart(event: any) {
		if (this.dropdownContainer() && this.translationX === 0) {
			this.computePosition();
		}
		this.updateAvailableHeight();
	}

	/**
	 * The container lives inside an `@if`, so it only exists after the view has
	 * been updated — measure on the next frame.
	 */
	private scheduleAvailableHeightUpdate(): void {
		requestAnimationFrame(() => this.updateAvailableHeight());
	}

	/**
	 * Publishes the height actually available to the dropdown as the
	 * `--dropdown-available-height` custom property, so panels can size
	 * themselves against it instead of guessing with a `vh` value.
	 *
	 * The limit is the viewport edge, or the edge of the nearest clipping
	 * ancestor when the dropdown sits inside a card that hides its overflow
	 * (the list toolbars do: their card is `overflow: hidden`, which is why a
	 * `60vh` panel was cut off on short screens).
	 */
	private updateAvailableHeight(): void {
		const container = this.dropdownContainer()?.nativeElement as HTMLElement | undefined;
		if (!container) {
			return;
		}

		const rect = container.getBoundingClientRect();
		let available: number;

		if (this.openAbove) {
			let boundaryTop = 0;
			for (let el = container.parentElement; el; el = el.parentElement) {
				if (getComputedStyle(el).overflowY !== 'visible') {
					boundaryTop = Math.max(boundaryTop, el.getBoundingClientRect().top);
				}
			}
			available = rect.bottom - boundaryTop;
		} else {
			let boundaryBottom = window.innerHeight;
			for (let el = container.parentElement; el; el = el.parentElement) {
				if (getComputedStyle(el).overflowY !== 'visible') {
					boundaryBottom = Math.min(boundaryBottom, el.getBoundingClientRect().bottom);
				}
			}
			available = boundaryBottom - rect.top;
		}

		const height = Math.max(MIN_AVAILABLE_HEIGHT_PX, available - BOUNDARY_MARGIN_PX);
		container.style.setProperty('--dropdown-available-height', `${Math.round(height)}px`);
	}

	protected computePosition() {
		const dropdownContainer = this.dropdownContainer();
		if (!dropdownContainer) {
			return;
		}
		const windowWidth = document.body.getBoundingClientRect().width
		const boxRight = dropdownContainer.nativeElement.getBoundingClientRect().right
		const boxWidth = dropdownContainer.nativeElement.getBoundingClientRect().width - 50;

		if (windowWidth - boxRight < this.minimumMargin) {
			this.translationX = -Math.min((this.minimumMargin - (windowWidth - boxRight)), boxWidth);
		}
	}

	open(trigger?: DropdownTriggerDirective) {
		this.triggerDirective = trigger;
		this.display = true;
		this.translationX = 0;
		this.repositionArrow();
		this.alignVerticallyToTrigger();
		this.scheduleAvailableHeightUpdate();
		this.addClickListener();
		this.opened.emit()
	}

	/**
	 * Aligns the dropdown host vertically with its trigger element by setting
	 * `top` (or `bottom` when `openAbove` is set) relative to the host's
	 * positioned ancestor. Only runs when `alignToTrigger` is enabled.
	 */
	private alignVerticallyToTrigger(): void {
		if (!this.alignToTrigger || !this.triggerDirective) {
			return;
		}

		const hostEl = this.elementRef.nativeElement as HTMLElement;
		const offsetParent = hostEl.offsetParent as HTMLElement | null;
		if (!offsetParent) {
			return;
		}

		const triggerRect = this.triggerDirective.elementRef.nativeElement.getBoundingClientRect();
		const parentRect = offsetParent.getBoundingClientRect();

		if (this.openAbove) {
			hostEl.style.top = '';
			hostEl.style.bottom = `${parentRect.bottom - triggerRect.bottom}px`;
		} else {
			hostEl.style.bottom = '';
			hostEl.style.top = `${triggerRect.top - parentRect.top}px`;
		}
	}

	close(trigger?: DropdownTriggerDirective) {
		this.triggerDirective = trigger;
		this.display = false;
		this.repositionArrow();
		this.removeClickListener();
		this.closed.emit()
	}

	private addClickListener(): void {
		document.addEventListener('click', this.onGlobalClickBound, true);
	}

	private removeClickListener(): void {
		document.removeEventListener('click', this.onGlobalClickBound, true);
	}

	private onGlobalClick(event: MouseEvent): void {
		const target = event.target as HTMLElement;

		if (target.closest('mat-datepicker-content') || target.closest('.mat-calendar-body-cell-content')) {
			return;
		}

		const rect = this.dropdownContainer()?.nativeElement?.getBoundingClientRect();
		const arrowSize = 15;

		const clickedInside: boolean = rect
			&& event.clientX > rect.x && event.clientX < (rect.x + rect.width)
			&& event.clientY > (rect.y + arrowSize) && event.clientY < (rect.y + arrowSize + rect.height);

		const clickedTrigger = this.triggerDirective?.elementRef.nativeElement.contains(target);

		if (this.display && !clickedInside && !clickedTrigger) {
			this.display = false;
			this.removeClickListener();
			this.closed.emit();
			// A raw document listener: nothing tells an OnPush host that the menu closed.
			this.changeDetector.markForCheck();
		}
	}
}
